import { Controller } from "@hotwired/stimulus";

// Frame L timers. Server enforcement is authoritative (Authentication
// concern); this controller only warns and tidies the exit. Values come
// from Rails.configuration.x via the shell — never hardcoded here.
export default class extends Controller {
  static targets = ["card", "countdown", "signOutForm"];
  static values = { warnAfter: Number, signOutAfter: Number, keepaliveUrl: String };

  connect() {
    this.activity = () => this.reset();
    ["pointerdown", "keydown"].forEach((e) => window.addEventListener(e, this.activity, { passive: true }));
    this.reset();
  }

  disconnect() {
    ["pointerdown", "keydown"].forEach((e) => window.removeEventListener(e, this.activity));
    this.clearTimers();
  }

  reset() {
    if (!this.cardTarget.classList.contains("hidden")) return; // ignore activity while warning shows
    this.clearTimers();
    this.warnTimer = setTimeout(() => this.warn(), this.warnAfterValue);
  }

  warn() {
    this.cardTarget.classList.remove("hidden");
    this.deadline = Date.now() + (this.signOutAfterValue - this.warnAfterValue);
    this.tick();
    this.tickTimer = setInterval(() => this.tick(), 1000);
  }

  tick() {
    const remaining = Math.max(0, Math.ceil((this.deadline - Date.now()) / 1000));
    const minutes = Math.floor(remaining / 60);
    const seconds = String(remaining % 60).padStart(2, "0");
    this.countdownTarget.textContent = `${minutes}:${seconds}`;
    if (remaining <= 0) this.expire();
  }

  // Reset only on an explicit 204 (Gate 2 finding 4): a failed or
  // redirected keepalive must not pretend the session was extended.
  async keepAlive() {
    const token = document.querySelector("meta[name='csrf-token']")?.content;
    try {
      const response = await fetch(this.keepaliveUrlValue, { method: "POST", headers: { "X-CSRF-Token": token } });
      if (response.status === 204) {
        this.cardTarget.classList.add("hidden");
        this.reset();
      } else {
        this.expire();
      }
    } catch {
      this.expire();
    }
  }

  // Auto-expiry is server-conditional (Gate 2 finding 4): a Turbo visit
  // lets the server decide — an expired session bounces to the roster with
  // the idle notice; a session kept alive elsewhere (another tab) simply
  // refreshes and this tab re-arms. Only the explicit "Sign out now"
  // button submits the DELETE.
  expire() {
    this.clearTimers();
    this.cardTarget.classList.add("hidden");
    window.Turbo.visit(window.location.href, { action: "replace" });
  }

  signOutNow() {
    this.clearTimers();
    this.signOutFormTarget.requestSubmit();
  }

  clearTimers() {
    clearTimeout(this.warnTimer);
    clearInterval(this.tickTimer);
  }
}
